import { useEffect, useRef, useState } from "react";
import { initializeMemberKeyVisual } from "./member-key-visual.js";
import { SystemMechanisms } from "./marketing-system-mechanisms.js";

export type MarketingPage = "overview" | "capabilities" | "system" | "future" | "members";

const pageMeta: Record<MarketingPage, { title: string; description: string }> = {
  overview: {
    title: "Basket — 仕事を一緒に進めるAIチーム",
    description: "調査、制作、日程調整、LEMO Appsまで。Slackで話しかけられるAI Team Basketの紹介です。",
  },
  capabilities: {
    title: "頼めること — Basket",
    description: "調査、画像生成、資料・ツール制作、業務サービスの操作、共有まで。Basketで利用できる機能の一覧です。",
  },
  system: {
    title: "仕組み — Basket",
    description: "Basket PolicyとAdaptive Context、対話を担うLuna・実務のWorker・独立Reviewerの構成、改善ループを紹介します。",
  },
  future: {
    title: "開発中の機能 — Basket",
    description: "Basket Meets、SELECTS Planningなど、開発中の機能を紹介します。",
  },
  members: {
    title: "メンバー — Basket",
    description: "AI Team Basketのメンバーと、それぞれの得意分野を紹介します。",
  },
};

const navItems: Array<{ page: MarketingPage; label: string }> = [
  { page: "overview", label: "トップ" },
  { page: "capabilities", label: "頼めること" },
  { page: "system", label: "仕組み" },
  { page: "future", label: "開発中の機能" },
  { page: "members", label: "メンバー" },
];

const connectedServices = [
  { name: "Asana", logo: "asana.png", detail: "マイタスクの確認、タスク・サブタスクの作成や編集、担当・期限の設定、コメント・メンション・添付。Todayの計画にもつなげます。" },
  { name: "Slack", logo: "slack.svg", detail: "DMやチャンネルで依頼し、同じスレッドで追加修正。過去のやりとりを探し、回答・ファイル・成果物のURLを受け取れます。" },
  { name: "Gmail", logo: "google-gmail.svg", detail: "メールのスレッドと添付を読み、最新のやりとりを確認。新規メールや添付付き返信を本人のGmailへ下書き保存できます。送信はしません。" },
  { name: "Google Drive", logo: "google-drive.svg", detail: "PDF・Excel・画像などの実ファイルを取得して、比較・加工・レビューに使います。成果物を本人のMy Driveへ保存し、ファイルやフォルダも整理できます。" },
  { name: "Google ドキュメント", logo: "google-docs.svg", detail: "調査レポートや議事メモを作成。既存文書の文章や書式も修正し、編集URLやPDFで受け取れます。" },
  { name: "Google スプレッドシート", logo: "google-sheets.svg", detail: "CSV・Excelから比較表・集計表を作成。既存シートの指定セルや書式も更新し、数式や他のタブを保って確認します。" },
  { name: "Google スライド", logo: "google-slides.svg", detail: "説明資料を作成し、既存スライドの文章・図形・色も修正。編集できるGoogleスライドやPDFで届けます。" },
  { name: "Google カレンダー", logo: "google-calendar.svg", detail: "予定と空き時間を確認し、予定の作成・変更、参加者の追加まで。終日予定や繰り返し予定の指定した回も扱えます。" },
  { name: "Google Meet", logo: "google-meet.svg", detail: "カレンダーの予定と一緒に会議URLを作成。会議にAIが同席するBasket Meetsは、別途開発中です。" },
] as const;

const serviceLogos = [
  ["Workspace Search", "/assets/marketing/services/workspace-search.svg", "Slack・Gmail・Drive・Asana・DocBaseを横断して高速に探す"],
  ["LEMO Files", "/assets/marketing/services/files.svg", "成果物を限定URLや認証付き共有で届ける"],
  ["LEMO Links", "/assets/marketing/services/links.svg", "長いURLをSELECTSのドメインで短くする"],
  ["LEMO Today", "/assets/marketing/services/today.svg", "計画・実績の編集、提出・再提出まで進める"],
  ["LEMO Auths", "/assets/marketing/services/auths-064ca2ca.svg", "本人のアカウントと権限を安全に結び付ける"],
  ["LEMO Apps DesignSystem", "/assets/marketing/services/apps-designsystem.svg", "業務ツールのロゴとUIを共通品質でつくる"],
  ["SELECTS Console CLI", "/assets/marketing/services/selects.svg", "本人のConsole接続で業務データを調べ、人気商品の集計をExcelで共有する"],
  ["SELECTS Specs", "/assets/marketing/services/selects.svg", "商品・料金・申込みなどのサービス仕様を確認する"],
  ["見積請求書ツール", "/assets/marketing/services/selects.svg", "SELECTS for Businessの見積書・請求書を作成、レビューする"],
] as const;

// Use each official lockup's design coordinates, excluding export padding.
// Scale the entire image from its wordmark size; keep marks, gaps and glows intact.
const serviceLockupMetrics: Record<string, { width: number; height: number; padding: number; fontSize: number }> = {
  "Workspace Search": { width: 586.2883522727273, height: 111, padding: 24, fontSize: 53 },
  "LEMO Files": { width: 249.80823863636363, height: 127, padding: 32, fontSize: 53 },
  "LEMO Links": { width: 264.9403409090909, height: 127, padding: 32, fontSize: 53 },
  "LEMO Today": { width: 290.6761363636364, height: 130, padding: 32, fontSize: 53 },
  "LEMO Auths": { width: 224.2784090909091, height: 76, padding: 0, fontSize: 52 },
  "LEMO Apps DesignSystem": { width: 621.5213068181818, height: 111, padding: 24, fontSize: 53 },
};

function ServiceBrand({ name, src }: { name: string; src: string }) {
  const metrics = serviceLockupMetrics[name];
  return <div className="marketing-service-logo marketing-service-logo--lemo">
    {metrics ? <span className="marketing-service-lockup" style={{
      width: `${(metrics.width - 2 * metrics.padding) / metrics.fontSize}em`,
      height: `${(metrics.height - 2 * metrics.padding) / metrics.fontSize}em`,
    }}><img src={src} alt={name} style={{
      width: `${metrics.width / metrics.fontSize}em`,
      height: `${metrics.height / metrics.fontSize}em`,
      left: `${-metrics.padding / metrics.fontSize}em`,
      top: `${-metrics.padding / metrics.fontSize}em`,
    }} /></span> : <><img className="marketing-service-mark" src={src} alt="" /><span className="marketing-service-name">{name}</span></>}
  </div>;
}

const activeMembers = [
  {
    name: "Melo",
    kana: "メロ",
    image: "/assets/marketing/members/melo.webp",
    role: "相談窓口",
    detail: "依頼を受け取り、必要な調査や制作を組み立てて進めます。迷ったときは、まずMeloへ。",
    status: "利用できます",
  },
  {
    name: "Peac",
    kana: "ピーク",
    image: "/assets/marketing/members/peac.webp",
    role: "難しい調査",
    detail: "時間をかける調査や比較、見落としの確認を担当します。巡回や監視ではなく、依頼された仕事に取り組みます。",
    status: "利用できます",
  },
  {
    name: "Kiw",
    kana: "キウ",
    image: "/assets/marketing/members/kiw.webp",
    role: "すばやい実行",
    detail: "簡単な質問や整理、短い作業を軽快に進めます。すぐ答えがほしい仕事が得意です。",
    status: "利用できます",
  },
  {
    name: "Pomel",
    kana: "ポメル",
    image: "/assets/marketing/members/pomel.webp",
    role: "代表専属",
    detail: "小川の意思決定と実務を支えます。利用できるのは小川本人だけです。",
    status: "小川専用",
  },
];

const futureMembers = [
  ["Banan", "バナン", "複数の案を同時に進め、成果をまとめる", "banan-v4-international-character.jpg"],
  ["Guav", "グァヴ", "大量の情報から、使える根拠を選び取る", "guav-v5-abstract-character.jpg"],
  ["Papay", "パパイ", "大きな仕事を、実行できる手順へ分ける", "papay-v5-abstract-character.jpg"],
  ["Duria", "ドリア", "難しい問題と不都合な事実を深く調べる", "duria-v5-female-character.jpg"],
  ["Mang", "マング", "複数の結果から中心となる論点を選ぶ", "mang-v5-abstract-character.jpg"],
  ["Coconu", "ココナ", "機密情報と利用範囲を安全に守る", "coconu-v5-abstract-character.jpg"],
  ["Citro", "シトロ", "まだ言葉になっていない未来を考える", "citro-v5-abstract-character.jpg"],
  ["Lemo", "レモ", "チームの判断を会社の方針へつなぐ", "lemo-v4-international-character.jpg"],
] as const;

export function marketingPageFromHash(hash: string): MarketingPage {
  const normalized = hash.replace(/^#\/?/u, "").replace(/\/$/u, "").toLowerCase();
  if (normalized === "capabilities") return "capabilities";
  if (normalized === "system") return "system";
  if (normalized === "future") return "future";
  if (normalized === "members") return "members";
  return "overview";
}

export function isBasketProductRoute(pathname: string): boolean {
  return pathname === "/extension"
    || pathname === "/desktop/connect"
    || pathname === "/desktop/connect/"
    || pathname.startsWith("/conversation/");
}

function pageHref(page: MarketingPage): string {
  return page === "overview" ? "/" : `/#/${page}`;
}

function SiteHeader({ page }: { page: MarketingPage }) {
  return <header className="marketing-header">
    <a className="marketing-brand" href="/" aria-label="Basket トップへ">
      <img src="/assets/brand/basket-logo.png" alt="Basket" width="880" height="283" />
    </a>
    <nav className="marketing-nav" aria-label="サイト内ナビゲーション">
      {navItems.map((item) => <a key={item.page} href={pageHref(item.page)} aria-current={page === item.page ? "page" : undefined}>{item.label}</a>)}
    </nav>
  </header>;
}

function SiteFooter() {
  return <footer className="marketing-footer">
    <div><img src="/assets/brand/basket-logo.png" alt="Basket" width="880" height="283" /></div>
    <nav aria-label="フッターナビゲーション">{navItems.map((item) => <a key={item.page} href={pageHref(item.page)}>{item.label}</a>)}</nav>
    <small>AI Team Basket · LEMO</small>
  </footer>;
}

function MemberWallpaper() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(() => matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    if (!containerRef.current) return;
    return initializeMemberKeyVisual(containerRef.current, { wallpaper: true, paused });
  }, [paused]);

  return <>
    <div ref={containerRef} className="marketing-member-wallpaper" aria-hidden="true"><canvas /></div>
    <button type="button" className="marketing-motion-toggle" aria-label={paused ? "背景のアニメーションを再生" : "背景のアニメーションを停止"} aria-pressed={paused} onClick={() => setPaused(!paused)}>
      <span aria-hidden="true">{paused ? "▶" : "Ⅱ"}</span>
    </button>
  </>;
}

function OverviewPage() {
  return <>
    <section className="marketing-hero marketing-shell">
      <div className="marketing-hero__copy">
        <p className="marketing-kicker">Slackで使える、LEMOのAIチーム</p>
        <h1><span>調査・制作から、</span><span>業務ツールの実行まで。</span></h1>
        <p className="marketing-lead">社内の記録を探す。画像やスライドをつくる。便利ツールをつくって、使えるURLで届ける。Google文書の作成・修正やGmailの下書き、Asana・Todayの更新まで、日々の仕事も進めます。</p>
        <p className="marketing-hero__members"><a href="/#/members">Melo・Peac・Kiwの得意分野を見る</a></p>
      </div>
      <img className="marketing-hero__image" src="/assets/marketing/scenes/basket-team-studio.webp" alt="調査や制作を一緒に進めるBasketのメンバー" width="1536" height="1024" />
    </section>
    <RequestExamples />
    <div className="marketing-capabilities-link marketing-shell">
      <a href="/#/capabilities">画像編集・データ加工など、すべての機能を見る</a>
    </div>
    <ServiceDetails />
  </>;
}

const requestExamples = [
  {
    kind: "research", service: "Workspace Search ＋ Google ドキュメント", member: "peac", name: "Peac",
    request: "この案件の問い合わせ履歴を探して、経緯をGoogleドキュメントにまとめて。",
    reply: "経緯と論点、参照したやりとりを文書にまとめ、あなたのDriveに保存しました。このURLから編集できます。",
    result: "出典つきのGoogleドキュメント・編集URL",
  },
  {
    kind: "page", service: "画像生成 ＋ LEMO Files", member: "melo", name: "Melo",
    request: "この商品の紹介ページをつくって。画像もお願い。",
    reply: "紹介文と画像を組み合わせて、ページをつくりました。このURLから開けます。",
    result: "画像入りの紹介ページ・公開URL",
  },
  {
    kind: "tool", service: "ツール制作 ＋ LEMO Files", member: "kiw", name: "Kiw",
    request: "画像をまとめてトリミングできるツールをつくって。",
    reply: "画像を選び、サイズをそろえて書き出せるツールをつくりました。ブラウザで使えます。",
    result: "その場で使えるツールのURL",
  },
  {
    kind: "game", service: "ゲーム制作 ＋ LEMO Files", member: "melo", name: "Melo",
    request: "忘年会で使うクイズをつくって。会場の画面に映したい。",
    reply: "問題と答えを切り替えられるクイズをつくりました。会場の画面で進行できます。",
    result: "問題・答え・進行画面つきのゲーム",
  },
  {
    kind: "slides", service: "資料制作 ＋ Google スライド", member: "melo", name: "Melo",
    request: "この資料を社内説明用のGoogleスライドにして。あとで自分で編集したい。",
    reply: "要点と説明の順序を整理し、Googleスライドに保存しました。このURLから開いて編集できます。",
    result: "本人のDriveに保存したGoogleスライド",
  },
  {
    kind: "calendar", service: "Google Calendar・Meet", member: "melo", name: "Melo",
    request: "来週の打ち合わせ候補を出して。決まったらMeetもつくって。",
    reply: "空き時間から候補を3つ出しました。日時が決まったら、あなたのカレンダーに予定とMeetをつくれます。",
    result: "候補日時 → 自分の予定・MeetのURL",
  },
  {
    kind: "today", service: "LEMO Today", member: "kiw", name: "Kiw",
    request: "資料確認は1.5時間で完了。Todayを更新して提出して。",
    reply: "資料確認の実績を1.5時間・完了に更新し、Todayを提出しました。提出した内容も確認しています。",
    result: "実績更新・完了設定・提出まで",
  },
  {
    kind: "asana", service: "Asana ＋ LEMO Today", member: "melo", name: "Melo",
    request: "このプロジェクトに自分担当・金曜締切でタスクを作って。Todayにも30分で入れて。",
    reply: "指定のプロジェクトにタスクを作り、Todayへ30分で追加しました。担当・期限・保存内容も確認しています。",
    result: "Asanaのタスク作成 → Todayの計画へ",
  },
  {
    kind: "sheets", service: "データ集計 ＋ Google スプレッドシート", member: "melo", name: "Melo",
    request: "この工程表の納期を直して。数式とほかのタブはそのままで。",
    reply: "元のスプレッドシートの指定セルを更新しました。数式とほかのタブを保って、保存後の内容も確認しています。",
    result: "元の工程表の納期を更新・数式と他タブは維持",
  },
  {
    kind: "estimate", service: "SELECTS for Business 見積請求書ツール", member: "melo", name: "Melo",
    request: "この内容で見積書のドラフトをつくって。計算も確認して。",
    reply: "ドラフトとPDFをつくりました。数量・単価・税・合計を確認し、確認が必要な点を添えています。",
    result: "見積書ドラフト・PDF・確認結果",
  },
  {
    kind: "gmail", service: "Gmail", member: "kiw", name: "Kiw",
    request: "このメールのやりとりと添付見積を確認して。返信を、資料付きで下書きにして。",
    reply: "最新の依頼と添付を確認し、同じメールのスレッドに返信下書きを保存しました。宛先・本文・添付を確認済みで、未送信です。",
    result: "本人のGmailに保存した下書き・未送信",
  },
  {
    kind: "share", service: "LEMO Files", member: "melo", name: "Melo",
    request: "この画像を、指定したメールアドレスの人だけに共有して。",
    reply: "指定した受取人だけが、メール認証をして開ける共有URLをつくりました。",
    result: "受取人を限定した共有URL",
  },
] as const;

function ResultPreview({ kind }: { kind: typeof requestExamples[number]["kind"] }) {
  switch (kind) {
    case "research": return <div className="marketing-mini-document"><div className="marketing-mini-google-title"><img src="/assets/marketing/services/google-docs.svg" alt="" /><strong>案件の経緯と論点</strong></div><div className="marketing-mini-topics"><span>発生経緯</span><span>対応状況</span><span>未確認の点</span></div><div className="marketing-mini-sources"><span>Slackの記録</span><span>メール</span><span>関連タスク</span></div><small>Googleドキュメントに保存済み</small></div>;
    case "page": return <div className="marketing-mini-page"><div><span>商品の紹介ページ</span><strong>贈る楽しみ、<br />選ぶ楽しみ。</strong><small>商品紹介・使い方・おすすめの用途</small></div><img src="/assets/marketing/scenes/selects-planning-operations.webp" alt="" loading="lazy" width="1536" height="1024" /></div>;
    case "tool": return <div className="marketing-mini-tool"><div className="marketing-mini-crop"><img src="/assets/marketing/scenes/basket-creative-workshop.webp" alt="" loading="lazy" width="1536" height="1024" /><span /></div><div><strong>画像トリミング</strong><span>正方形 ／ 横長</span><span>まとめて書き出し</span></div></div>;
    case "game": return <div className="marketing-mini-quiz"><span>忘年会クイズ　01 / 10</span><strong>1年で、31日ある月はいくつ？</strong><div><span>A　6か月</span><span>B　7か月</span><span>C　8か月</span></div></div>;
    case "slides": return <div className="marketing-mini-slides"><div className="marketing-mini-slide"><span>社内説明資料</span><strong>企画の背景と<br />進め方</strong><div className="marketing-mini-slide-bars"><i /><i /><i /></div></div><div className="marketing-mini-file-list"><span className="marketing-mini-google-title"><img src="/assets/marketing/services/google-slides.svg" alt="" />Google スライド</span><span>社内説明資料<small>自分のDriveに保存済み</small></span></div></div>;
    case "calendar": return <div className="marketing-mini-calendar"><strong>30分の打ち合わせ</strong><div><span>候補 A</span><b>10:00–10:30</b></div><div><span>候補 B</span><b>14:00–14:30</b></div><div><span>候補 C</span><b>16:00–16:30</b></div></div>;
    case "today": return <div className="marketing-mini-plan"><strong>今日のToday <small>提出済み</small></strong><div><span>資料確認</span><small>1.5時間・完了</small></div><div><span>回答作成</span><small>未完了</small></div><div><span>提出した内容</span><small>履歴に保存</small></div></div>;
    case "asana": return <div className="marketing-mini-plan"><strong>資料を準備する <small>Asana</small></strong><div><span>担当</span><small>自分</small></div><div><span>期限</span><small>金曜日</small></div><div><span>Todayの計画</span><small>0.5時間・追加済み</small></div></div>;
    case "sheets": return <div className="marketing-mini-sheet"><div className="marketing-mini-google-title"><img src="/assets/marketing/services/google-sheets.svg" alt="" /><strong>既存の工程表</strong></div><table><thead><tr><th>工程</th><th>納期</th><th>状態</th></tr></thead><tbody><tr><td>入稿</td><td>11/20</td><td>維持</td></tr><tr><td>納品</td><td>11/27</td><td>更新済み</td></tr></tbody></table><small>数式とほかのタブを維持・保存後に確認済み</small></div>;
    case "estimate": return <div className="marketing-mini-estimate"><strong>御見積書 <small>ドラフト</small></strong><div className="marketing-mini-estimate-columns"><span>品名</span><span>数量</span><span>単価</span><span>金額</span></div><div className="marketing-mini-estimate-lines" /><span>数量・単価・税・合計を確認</span></div>;
    case "gmail": return <div className="marketing-mini-mail"><div className="marketing-mini-google-title"><img src="/assets/marketing/services/google-gmail.svg" alt="" /><strong>Re: 見積書の確認 <small>下書き</small></strong></div><dl><div><dt>To</dt><dd>依頼メールの送信者</dd></div><div><dt>添付</dt><dd>見積の比較表.xlsx</dd></div></dl><p>最新のご依頼と添付を確認しました。<br />修正点を比較表にまとめています。</p><small>同じメールのスレッドに保存済み・未送信</small></div>;
    case "share": return <div className="marketing-mini-share"><img src="/assets/marketing/services/files.svg" alt="LEMO Files" /><strong>画像ファイル</strong><span>指定したメールアドレスの受取人</span><small>メール認証付きで共有</small></div>;
  }
}

function RequestExamples() {
  return <section className="marketing-request-examples marketing-shell" aria-labelledby="request-examples-title">
    <div className="marketing-request-examples__heading"><h2 id="request-examples-title">依頼例</h2><p>応答・成果物はイメージです。</p></div>
    <div className="marketing-request-grid">{requestExamples.map((example) => <article className="marketing-request-card" key={example.kind}>
      <blockquote className="marketing-request-bubble">{example.request}</blockquote>
      <div className="marketing-example-reply">
        <img src={`/assets/marketing/members/${example.member}.webp`} alt="" loading="lazy" width="40" height="40" />
        <div><span className="marketing-example-speaker">{example.name}</span><p>{example.reply}</p></div>
      </div>
      <figure className={`marketing-result-preview marketing-result-preview--${example.kind}`}>
        <ResultPreview kind={example.kind} />
        <figcaption>{example.result}</figcaption>
      </figure>
      <p className="marketing-example-service">{example.service}</p>
    </article>)}</div>
  </section>;
}

const capabilityGroups = [
  {
    icon: "/assets/marketing/icons/research.svg",
    label: "相談・調査",
    examples: [
      ["すばやい相談・文章づくり", "簡単な質問、比較、アイデア、メールや案内文の下書きをすばやく返す"],
      ["性能が必要な難しい仕事", "高性能なAIモデルで、複雑な条件の比較、推論、長い資料を使った調査を進める"],
      ["難しい資料の要約・解説", "長いレポートや専門的な内容を読み、判断に必要な点を目的に合わせて説明する"],
      ["企業・サービス・事例調査", "公開Webを調べ、出典、確認できた事実、まだ分からない点を分けてまとめる"],
      ["複数資料の比較", "複数の文書やURLをまたいで、共通点、違い、判断材料を整理する"],
      ["社内情報を横断検索", "Slack、Gmail、Drive、Asana、DocBaseから、案件や過去のやりとりを探す"],
      ["SELECTSの仕様確認", "SELECTS Specsをもとに、商品、料金、申込み、配送などのサービス仕様を確認する"],
    ],
  },
  {
    icon: "/assets/marketing/icons/create.svg",
    label: "制作・加工",
    examples: [
      ["新しい画像をつくる", "用途や雰囲気を伝えて、キービジュアル、イラスト、背景素材などを生成する"],
      ["画像を編集する", "背景除去、色変更、トリミング、サイズ統一に加え、同じ会話で追加修正する"],
      ["商品や企画の紹介ページ", "紹介文、画像、レイアウトをまとめて制作。内容やデザインの指定にも対応する"],
      ["ブラウザで使える便利ツール", "画像トリミング、CSV比較、一覧確認など、その場で動く小さな業務ツールをつくる"],
      ["すぐ遊べるゲーム", "忘年会のクイズやビンゴなど、ブラウザですぐ遊べるゲームをつくる"],
      ["編集できる説明スライド", "説明内容をスライドにまとめ、編集できるPowerPointと確認用PDFで届ける"],
      ["表の比較・加工・集計", "CSV・Excelの比較、差分抽出、集計、形式の変換を行い、結果と計算条件をまとめる"],
      ["複数ファイルを一括加工", "複数画像の変換、サイズ統一、名前変更を行い、一覧とZIPにまとめる"],
      ["業務ツールのロゴとUI", "LEMO Apps DesignSystemを使い、ロゴや画面部品をつくる。指定があれば別のデザインにも対応"],
    ],
  },
  {
    icon: "/assets/marketing/services/google-drive.svg",
    label: "Googleでつくる・直す・保存する",
    examples: [
      ["「調査結果をGoogleドキュメントにまとめて」", "案件の経緯、比較結果、参照元を新しい文書に整理し、本人のDriveへ保存。ブラウザで編集できるURLを返す"],
      ["「このCSVを集計してGoogleスプレッドシートにして」", "月別・商品別などの集計表や比較表を作成し、数式と計算結果も確認して保存する"],
      ["「この資料を説明用のGoogleスライドにして」", "内容と説明順を整理して新規保存。受け取ったURLから、自分で文章やレイアウトを編集できる"],
      ["「案内メールをGmailの下書きに保存して」", "宛先・CC・BCC・件名・本文を入れた新規下書きを本人のGmailへ保存。返信では元のスレッドと添付も確認する"],
      ["「DriveのPDFとメール添付の見積を比べて」", "実ファイルを取得して数値・条件・最新版を照合。見積の専用レビューへ渡し、根拠と修正点を示す"],
      ["「このシート・文書・スライドの一部を直して」", "元ファイルの指定したセル、文章、図形、書式を更新。他の内容を保ち、保存後の実物も確認する"],
      ["「資料をExcelやPDFでも書き出して」", "Google文書の編集URLに加え、指定形式のファイルを取得してFilesで共有する"],
    ],
  },
  {
    icon: "/assets/marketing/icons/execute.svg",
    label: "業務サービス",
    examples: [
      ["空き時間と候補日を出す", "Google Calendarの予定を確認し、指定時間に合う候補を複数提案する"],
      ["予定とGoogle Meetをつくる", "日時や内容が決まったら、自分のカレンダーに予定とGoogle Meetの会議URLを作成する"],
      ["繰り返し・終日予定を変更", "指定した回だけ、またはシリーズ全体の日時・件名・参加者を変更。通知先と保存内容も確認する"],
      ["毎週の集計や計画更新を登録", "指定日時に情報を取得し、本人のGoogle資料やTodayを更新して同じスレッドへ報告。依頼の変更・停止もできる"],
      ["今日と前回稼働日の仕事を確認", "LEMO Todayの予定、実績、未完了を区別し、今日の計画に必要な情報をまとめる"],
      ["計画・実績を編集して提出", "Todayの行追加、実績時間、完了状態、順番、日別コメントを更新して提出する。提出済みの訂正・再提出や、削除した行の復元もできる"],
      ["AsanaからTodayまでまとめて計画", "本人のマイタスクとTodayを照合し、期限の近い仕事の漏れや期限のずれを確認。追加の依頼があれば、タスクを取り込んで予定時間を設定する"],
      ["Asanaのタスクを作成・編集", "プロジェクト・セクション・担当者・期限を指定してタスクやサブタスクを作成し、期限を変更する。TodayのAsana直接ログイン、またはAuthsの本人接続を使う"],
      ["Asanaのコメント・メンション・添付", "本人名義でコメントし、指定相手へのメンションやSlackで渡したファイルの添付まで進める。自分へのメンションは取得できた範囲と原文を示す（Inbox全件・未読状態の取得ではありません）"],
      ["Consoleの人気商品を集計してExcelで共有", "案件を指定し、第一希望の人気商品を集計。ExcelをFilesの共有URLで届け、確認用のカレンダー予定にも同じURLを添える。Consoleの本人接続が必要"],
      ["見積書・請求書をつくって確認", "見積請求書ツールでドラフトとPDFを作成。数量、単価、税、合計の確認も頼める"],
      ["URLを一件でもまとめてでも短縮", "LEMO Linksで、1件から複数URLまでSELECTSドメインで短縮。元URLとの対応表も返す"],
    ],
  },
  {
    icon: "/assets/marketing/icons/deliver.svg",
    label: "共有・追加修正",
    examples: [
      ["Webツールやページをホスティング", "HTML、CSS、画像などをまとめ、ブラウザでそのまま使えるURLとして公開する"],
      ["標準の限定共有", "指定がなければ、総アクセス150回・保持60日の限定URLをLEMO Filesでつくる"],
      ["受取人を限定して共有", "指定したメールアドレスの人や、Authsで認証した従業員だけが受け取れるように共有する"],
      ["Filesが使えないときも届ける", "認証切れや技術的な失敗で諦めず、条件を守れる場合はSlackへの直接添付を試す"],
      ["複数の成果物をまとめて納品", "画像、PDF、PowerPoint、CSV、HTMLなどを、個別ファイルやZIPにまとめて届ける"],
      ["同じ会話で続きを直す", "「2枚目だけ」「色だけ」のような追加依頼を、直前の正しい成果物へ反映する"],
      ["別の会話やメンバーへ続きを頼む", "自分が以前受け取った成果物を指定し、元のファイルを取り出して追加修正する"],
      ["開ける・動くところまで確認", "共有URLが開けるか、ファイルの内容やツールの主要操作が意図どおりかを確認する"],
    ],
  },
];

function CapabilityDetails() {
  return <div className="marketing-capability-groups">
    {capabilityGroups.map((group) => <article className="marketing-capability-group" key={group.label}>
      <div className="marketing-capability-group__heading">
        <img className="marketing-capability-icon" src={group.icon} alt="" aria-hidden="true" />
        <h2>{group.label}</h2>
      </div>
      <dl>{group.examples.map(([term, detail]) => <div key={term}><dt>{term}</dt><dd>{detail}</dd></div>)}</dl>
    </article>)}
  </div>;
}

function CapabilitiesPage() {
  return <>
    <h1 className="marketing-visually-hidden">Basketに頼めること</h1>
    <section className="marketing-full-capabilities marketing-shell" aria-label="機能一覧">
      <CapabilityDetails />
    </section>
    <ServiceDetails />
    <aside className="marketing-feedback-link marketing-shell">
      <p>うまくいかなかったことや、欲しい機能も伝えてください。日々の利用から改善点を集め、アップデートにつなげています。</p>
      <a href="/#/system">改善の仕組みを見る</a>
    </aside>
  </>;
}

function ServiceDetails() {
  return <>
    <section className="marketing-services" aria-labelledby="services-title">
      <div className="marketing-shell">
        <div className="marketing-section-heading"><h2 id="services-title">主な対応サービス</h2><p>会社で接続済みのサービスを、Slackから本人として利用できます。追加の接続が必要な場合だけ案内します。</p></div>
        <div className="marketing-service-grid marketing-service-grid--connected">{connectedServices.map((service) => <article key={service.name}>
          <div className="marketing-service-logo marketing-service-logo--symbol"><img src={`/assets/marketing/services/${service.logo}`} alt="" loading="lazy" /><span>{service.name}</span></div>
          <p>{service.detail}</p>
        </article>)}</div>
        <h3 className="marketing-service-group-title">LEMO Apps</h3>
        <div className="marketing-service-grid">{serviceLogos.map(([name, src, detail]) => <article key={name}><ServiceBrand name={name} src={src} /><p>{detail}</p></article>)}</div>
      </div>
    </section>
  </>;
}

function SystemPage() {
  const learningLoop: Array<[string, string]> = [
    ["要望・間違い・失敗を自動で集める", "普段のやりとりから、機能の要望、回答への訂正、完了できなかった仕事を週次で回収します。"],
    ["改善点を見つけ、要件を決める", "AIが改善候補を整理。採用した改善は、Basket Policyと業務ルールに沿って具体化します。"],
    ["知識・スキル・機能を更新する", "説明の誤りを直し、仕事の進め方や機能を整えます。同じ種類の依頼にも役立つ改善にします。"],
    ["検証した改善を届け、次の利用へ", "Slackの自動テストで動作を確かめてから反映。アップデート後の利用やフィードバックが、次の改善につながります。"],
  ];
  const stabilityLoop: Array<[string, string]> = [
    ["スタッフと同じSlackから依頼する", "検証用アカウントで、DMやチャンネルから実際に依頼します。"],
    ["答えと成果物を使って確かめる", "回答の内容に加え、添付ファイル、共有URL、つくったツールが期待どおり使えるか確認します。"],
    ["原因を見つけて修正する", "止まった処理や誤った結果の原因を調べ、機能やルールを修正します。"],
    ["同じ依頼で、もう一度試す", "修正後も自動テストを繰り返し、期待する動作を確認。似た依頼への悪影響も調べます。"],
  ];
  return <>
    <h1 className="marketing-visually-hidden">Basketの仕組み</h1>
    <SystemMechanisms />

    <section className="marketing-loop-section" aria-labelledby="improvement-title">
      <MemberWallpaper />
      <div className="marketing-shell marketing-loop-content">
        <div className="marketing-loop-intro">
          <h2 id="improvement-title">間違いや失敗、機能の不足を、継続的に改善します。</h2>
          <p>やりたいこと、気づいたことをそのまま伝えてください。普段のやりとりから改善点を自動で集め、学習・機能開発と、動作を確かめるテストを重ねてアップデートにつなげます。</p>
        </div>
        <div className="marketing-loop-grid">
          <article className="marketing-loop-card">
            <h3>学習と要件定義・機能開発のループ</h3>
            <ol>{learningLoop.map(([name, detail]) => <li key={name}><strong>{name}</strong><p>{detail}</p></li>)}</ol>
          </article>
          <article className="marketing-loop-card">
            <h3>提供機能安定化のためのループ</h3>
            <ol>{stabilityLoop.map(([name, detail]) => <li key={name}><strong>{name}</strong><p>{detail}</p></li>)}</ol>
          </article>
        </div>
      </div>
    </section>
  </>;
}

function FuturePage() {
  return <>
    <h1 className="marketing-visually-hidden">開発中の機能</h1>
    <section className="marketing-future-list marketing-shell">
      <article className="marketing-future-feature">
        <img src="/assets/marketing/scenes/basket-meets.webp" alt="オンライン会議を支援するBasket Meets" width="1536" height="1024" />
        <div>
          <p className="marketing-status">Basket Meets · 開発中</p>
          <h2>会議に同席し、話者を聞き分けながらサポート</h2>
          <p>Google MeetやZoomで会話を整理し、関連情報、論点、決定事項、タスク候補を会議ボードへまとめます。</p>
          <ul><li>誰が何を話したか、話者ごとに文字起こし</li><li>会議中の情報や決定事項を、ひとつのボードに整理</li><li>音声で質問し、会議の内容について会話</li></ul>
        </div>
      </article>
      <div className="marketing-future-feature">
        <img src="/assets/marketing/scenes/selects-planning-operations.webp" alt="カタログギフトの企画と業務を進めるBasketのメンバー" width="1536" height="1024" loading="lazy" />
        <div className="marketing-specialist-features">
          <article>
            <p className="marketing-status">SELECTS Planning · 開発中</p>
            <h2>カタログギフトの企画・プランニング・レビュー</h2>
            <p>贈る相手、目的、予算から要件を整理。過去事例や業務ルールを参考に、商品候補や構成を提案し、プランをレビューします。</p>
          </article>
        </div>
      </div>
    </section>
  </>;
}

function MembersPage() {
  return <>
    <section className="marketing-members marketing-shell" aria-label="現在のメンバー">
      <h1 className="marketing-visually-hidden">Basketのメンバー</h1>
      <div className="marketing-members__grid">{activeMembers.map((member) => <article key={member.name}><img src={member.image} alt={`${member.name}のプロフィール`} width="320" height="320" /><div className="marketing-member-body"><div className="marketing-member-name"><h2>{member.name}</h2><span>{member.kana}</span></div><p className="marketing-member-role">{member.role}</p><p>{member.detail}</p><span className="marketing-member-status">{member.status}</span></div></article>)}</div>
    </section>
    <section className="marketing-future-members marketing-shell" aria-labelledby="future-members-title">
      <div className="marketing-section-heading"><h2 id="future-members-title">準備中のメンバー</h2></div>
      <div className="marketing-future-members__grid">{futureMembers.map(([name, kana, detail, image]) => <article key={name}><img src={`/assets/member-directory/${image}`} alt={`${name}のプロフィール`} width="1000" height="1000" loading="lazy" /><div><h3>{name}<span>{kana}</span></h3><p>{detail}</p><small>準備中</small></div></article>)}</div>
    </section>
  </>;
}

export function MarketingSite() {
  const [page, setPage] = useState<MarketingPage>(() => marketingPageFromHash(window.location.hash));

  useEffect(() => {
    const sync = () => setPage(marketingPageFromHash(window.location.hash));
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  useEffect(() => {
    const meta = pageMeta[page];
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [page]);

  return <div className={`marketing-site marketing-site--${page}`}>
    <SiteHeader page={page} />
    <main>
      {page === "overview" ? <OverviewPage /> : null}
      {page === "capabilities" ? <CapabilitiesPage /> : null}
      {page === "system" ? <SystemPage /> : null}
      {page === "future" ? <FuturePage /> : null}
      {page === "members" ? <MembersPage /> : null}
    </main>
    <SiteFooter />
  </div>;
}
